'use client';

import { getCookies } from 'cookies-next';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
} from 'nuqs';
import { useEffect, useState } from 'react';

import {
  maxAge,
  minAge,
  vulnerableCountryIsoCodes,
} from '@sola/contentful-model/lib/sac-values.js';
import { childSearch, getRandomChildren } from '@sola/proxy-sdk/children.js';
import {
  ChildAvailabilityItem,
  ChildAvailabilitySearchResults,
} from '@sola/schema/dotcom/children/child.js';
import { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.js';
import { SacFilterProps } from '@sola/schema/lib/schema.js';
import { useSessionStorage } from '@sola/shared-utils/session-storage/session-storage.js';

const defaultLimit = 12;

export const useSacChildren = (
  filters: SacFilterProps,
  allowShowMore: boolean,
) => {
  const [page, setPage] = useState<number>(0);
  const [prevPage, setPrevPage] = useState<number>(0);
  const [maxPages] = useState<number>(9);
  const [gender] = useQueryState(
    'gender',
    filters?.gender
      ? parseAsString.withDefault(filters?.gender)
      : parseAsString,
  );
  const [ageMin] = useQueryState(
    'ageMin',
    filters?.ageMin
      ? parseAsInteger.withDefault(filters?.ageMin)
      : parseAsInteger,
  );
  const [ageMax] = useQueryState(
    'ageMax',
    filters?.ageMax
      ? parseAsInteger.withDefault(filters?.ageMax)
      : parseAsInteger,
  );
  const [countries] = useQueryState(
    'countries',
    filters?.countries
      ? parseAsArrayOf(parseAsString).withDefault([
          ...(filters?.countries || ''),
        ])
      : parseAsArrayOf(parseAsString),
  );
  const [birthDay] = useQueryState('birthDay', parseAsString);
  const [birthMonth] = useQueryState('birthMonth', parseAsString);
  const useAttributes = () => {
    const [queryFilters] = useQueryState('filters', parseAsString);
    const disability = queryFilters?.includes('hasDisability') || null;
    const isInHivAffectedArea = queryFilters?.includes('affectedHiv') || null;
    const isOrphan = queryFilters?.includes('orphanedChild') || null;
    const isVulnerable =
      queryFilters?.includes('vulnerableExploitation') || null;
    return { disability, isInHivAffectedArea, isOrphan, isVulnerable } as const;
  };
  const { disability, isInHivAffectedArea, isOrphan, isVulnerable } =
    useAttributes();
  const [filteredCountries, setFilteredCountries] = useState(
    countries ? [...(countries || '')] : undefined,
  );
  const { limit = defaultLimit } = filters;
  const [children, setChildren] = useState<ChildAvailabilityItem[]>([]);
  const [storedChildren, setStoredChildren] = useState<ChildAvailabilityItem[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [forwardOrBackNav, setForwardOrBackNav] = useState(true);
  const pathname = usePathname();
  const router = useRouter();
  const cookieStore = getCookies();
  const resetFilters = () => router.push(pathname);
  const [showLoadMoreButton, setShowLoadMoreButton] = useState<boolean>(true);
  const params = useSearchParams();

  const {
    get: getFromSessionStorage,
    set: setInSessionStorage,
    compare: compareSessionStorage,
    remove: removeSessionStorage,
    readChildStore,
  } = useSessionStorage();

  const hasCriteria = () => {
    return !!(
      gender ||
      (ageMin && ageMax
        ? ageMin !== minAge || ageMax !== maxAge
        : ageMin || ageMax) ||
      countries?.length ||
      birthDay ||
      birthMonth ||
      isInHivAffectedArea ||
      disability ||
      isOrphan ||
      isVulnerable
    );
  };

  const visitedPreviously = (newParams: string) => {
    return (
      compareSessionStorage(
        ['sacRef', 'sacParams'],
        [String(cookieStore.referrer), newParams],
      ) && getFromSessionStorage('sacChildren') !== null
    );
  };

  const storeResults = (
    currentPage: number,
    results?: ChildAvailabilitySearchResults,
  ) => {
    setStoredChildren(() => [
      ...((results?.children as ChildAvailabilityItem[]) || []),
    ]);
    setChildren(() => [
      ...((results?.children as ChildAvailabilityItem[]).slice(
        0,
        limit + currentPage * limit,
      ) || []),
    ]);
    setShowLoadMoreButton(
      allowShowMore && !!results?.children && results.children.length >= limit,
    );
    setIsLoading(false);
  };
  const previousResults = (results: ChildAvailabilitySearchResults) => {
    const previousResultsPage = Number.isNaN(getFromSessionStorage('sacPage'))
      ? 0
      : Number(getFromSessionStorage('sacPage'));
    setPrevPage(previousResultsPage);
    setPage(previousResultsPage);
    storeResults(previousResultsPage, results);
  };

  const saveVisit = (
    currentPage: number,
    results?: ChildAvailabilitySearchResults,
  ) => {
    setInSessionStorage(
      ['sacChildren', 'sacRef', 'sacParams', 'sacPage'],
      [
        JSON.stringify(results),
        String(cookieStore.referrer),
        params.toString(),
        '0',
      ],
    );
    storeResults(currentPage, results);
  };

  const incrementPage = () => {
    if (page < maxPages) {
      setForwardOrBackNav(false);
      setPrevPage(page);
      setPage(page + 1);
      setInSessionStorage('sacPage', Number(page + 1).toString());
    } else {
      setShowLoadMoreButton(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      let reset = false;
      setChildren([]);
      setStoredChildren([]);
      setIsLoading(true);
      if (!(prevPage === 0 && page > 0)) {
        setPage(0);
        reset = true;
      } else {
        setPrevPage(page);
      }
      const tempResults = readChildStore();
      if (visitedPreviously(params.toString()) && tempResults !== null) {
        if (!cancelled) {
          previousResults(tempResults);
        }
      } else if (hasCriteria()) {
        const results = await childSearch({
          take: limit * 10,
          skip: 0,
          gender: gender as ChildGenderEnum,
          ageMin,
          ageMax,
          countries: isVulnerable ? filteredCountries : countries,
          birthDay,
          birthMonth,
          isOrphan,
          isInHivAffectedArea,
          disability,
          referrer: cookieStore.referrer,
        });
        if (!cancelled) {
          saveVisit(reset ? 0 : page, results);
        }
      } else if (!hasCriteria()) {
        const results = await getRandomChildren(limit * 10);
        if (!cancelled) {
          saveVisit(reset ? 0 : page, results);
        }
      }
    };
    fetchData();
    return (): void => {
      cancelled = true;
    };
  }, [
    gender,
    ageMax,
    ageMin,
    filteredCountries,
    disability,
    isInHivAffectedArea,
    isOrphan,
    birthDay,
    birthMonth,
    params,
  ]);

  const scrollToElement = (childId?: string) => {
    if (childId && childId !== '') {
      let counter = 50;
      const waitForElement = setInterval(() => {
        const childElement = document.querySelector(
          `.SacCard__bio-link[href*="${childId}"]`,
        );
        if (childElement) {
          childElement.scrollIntoView({ behavior: 'instant', block: 'start' });
          clearInterval(waitForElement);
        }
        if (counter === 0) {
          clearInterval(waitForElement);
        }
        counter--;
      }, 50);
    }
  };

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      window.history &&
      'scrollRestoration' in window.history
    ) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    const selectedCountries = countries ? [...(countries || '')] : undefined;
    // Highly vulnerable is just a list of countries...
    if (isVulnerable) {
      const hvc = vulnerableCountryIsoCodes as readonly string[];
      setFilteredCountries(
        selectedCountries?.length
          ? selectedCountries.filter((c) => hvc.includes(c.toString()))
          : [...vulnerableCountryIsoCodes],
      );
      if (!filteredCountries?.length) {
        // Did they have a country selected that isn't one of the HVC countries - return to the selected country
        setFilteredCountries(selectedCountries);
        // TOOD: inform the user of the conflict? update the interface to reflect this?
      }
    }
  }, [countries, isVulnerable]);

  useEffect(() => {
    const childAmount = (page + 1) * limit;
    const isLastPage = page >= maxPages;

    const nextChildren = storedChildren.slice(0, childAmount);
    setChildren(nextChildren);

    const firstChildOfPage = storedChildren[page * limit]?.globalId;

    const hasMore = storedChildren.length > nextChildren.length;
    setShowLoadMoreButton(!isLastPage && hasMore);

    if (page === 0) {
      document
        .querySelector('.SacCard__bio-link')
        ?.scrollIntoView({ behavior: 'instant', block: 'end' });
      return;
    }

    if (forwardOrBackNav) {
      scrollToElement(firstChildOfPage);
    } else {
      setForwardOrBackNav(true);
    }
  }, [page, storedChildren]);

  useEffect(() => {
    if (!isLoading) {
      if (getFromSessionStorage('sacSelectedChild')) {
        scrollToElement(getFromSessionStorage('sacSelectedChild') || '');
        removeSessionStorage('sacSelectedChild');
      }
    }
  }, [isLoading]);

  return {
    isLoading,
    children,
    showRichMix: !hasCriteria(),
    showLoadMoreButton,
    incrementPage,
    resetFilters,
    limit,
  };
};
